import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { baseUrl } from "./Components/baseUrl";
import { prefetchShopProducts } from "./Components/shopProductsPrefetch";
import {
  readProductListCache,
  readShopListCache,
  writeProductListCache,
  writeShopListCache,
} from "./Components/shopListCache";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";
import Wallete from "./Components/Wallete";
import CallModal from "./Components/CallModal";
import GiftModal from "./Components/GiftModal";
import DescriptionModal from "./Components/DescriptionModal";
import MapModal from "./Components/MapModal";
import SortModal from "./Components/SortModal";
import ProductSortModal from "./Components/ProductSortModal";
import ProductFilterModal from "./Components/ProductFilterModal";
import AppIcon from "./Components/AppIcon";
import { SearchBarAddShopIcon, SearchBarProductFilterIcon } from "./Components/SearchBarIcons";
import { createSearchEnterHandler } from "./Components/searchInputHelpers";
import OrderInactivityFilterModal, {
  OrderInactivityIntroModal,
} from "./Components/OrderInactivityFilterModal";
import packageJson from "./../package.json";
import { getAuthToken } from "./Components/authSession";
import { getApiErrorMessage } from "./Components/apiErrorHelpers";
import { useVisitorLanguage } from "./Components/useVisitorLanguage";
import Gallery from "./Components/Gallery";
import ShopCrmModal from "./Components/ShopCrmModal";
import CreditPaymentBlockModal from "./Components/CreditPaymentBlockModal";
import {
  fetchCreditPaymentBlockStatus,
  navigateToCreditPayment,
  applyCreditBlockFromStatus,
} from "./Components/creditPaymentBlock";
import { getShopCreditAlertClass } from "./Components/shopCreditAlert";
import ShopCreditLegend from "./Components/ShopCreditLegend";
import ShopCardMeta from "./Components/ShopCardMeta";
import ShopInsightFilterModal, { ShopInsightFilterButton } from "./Components/ShopInsightFilterModal";
import {
  appendShopInsightFilters,
  hasActiveShopInsightFilter,
  loadShopInsightFilter,
  saveShopInsightFilter,
} from "./Components/shopInsightFilterHelpers";
import VisitorDailyDashboard from "./Components/VisitorDailyDashboard";
import { translation } from "./Components/Translation";
import {
  calcCustomerProfitPercent,
  formatCustomerProfitPercent,
} from "./Components/productUnitHelpers";
import { LoadMoreSkeleton, ProductListSkeleton, ShopListSkeleton } from "./Components/SkeletonLoading";
import {
  getProductCardClassName,
} from "./Components/productExpiryHelpers";
import {
  buildProductListUrl,
  hasActiveProductFilter,
  hasActiveProductSort,
  orderProductListForDisplay,
  resolveProductListHasMore,
} from "./Components/productListQueryHelpers";
import { scrollPageToTop } from "./Components/scrollPageToTop";

const buildShopsUrl = (
  searchText,
  sortBy,
  userCoords,
  page = null,
  noOrderFilter = "",
  insightFilter = null
) => {
  const params = new URLSearchParams();
  params.set("count", "20");
  if (searchText) {
    params.set("search", searchText);
  }
  if (page) {
    params.set("page", page);
  }
  if (sortBy) {
    params.set("sort", sortBy);
  }
  if (noOrderFilter) {
    params.set("no_order_days", noOrderFilter);
  }
  if (sortBy === "nearest" && userCoords) {
    params.set("lat", userCoords.lat);
    params.set("lng", userCoords.lng);
  }
  appendShopInsightFilters(params, insightFilter || loadShopInsightFilter());
  return `${baseUrl}/shops?${params.toString()}`;
};

const Shop = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const lang = useVisitorLanguage();
  const [storeName, setStoreName] = useState("");
  let token;
  let currency;
  if (typeof window !== "undefined") {
    token = localStorage.getItem("token");
    currency = localStorage.getItem("currency");
  }

  useEffect(() => {
    document.title = "ویزیت | فروشگاه";
    if (!getAuthToken()) {
      navigate("/login", { replace: true });
      return;
    }
    let version = localStorage.getItem("version");
    if (version != packageJson.version) {
      const preservedKeys = [
        "token",
        "lang",
        "currency",
        "shopSort",
        "shopNoOrderFilter",
        "shopInsightFilter",
      ];
      const preserved = preservedKeys.reduce((acc, key) => {
        const value = localStorage.getItem(key);
        if (value != null) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if ("caches" in window) {
        caches.keys().then((names) => {
          Promise.all(names.map((name) => caches.delete(name)));
        });
      }

      try {
        sessionStorage.removeItem("visitor_profile_cache");
        sessionStorage.removeItem("visitor-order-list:");
      } catch (_) {
        // ignore
      }

      localStorage.removeItem("vizit_shop_reference_cache_v1");
      localStorage.removeItem("visitor_permissions");
      localStorage.clear();
      Object.entries(preserved).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      localStorage.setItem("version", packageJson.version);
    }
  }, []);

  const [activeMenu, setActiveMenu] = useState(0);
  const closeShopQuickActions = () => {
    setActiveMenu(0);
  };
  const openMenuHandler = (item) => {
    setActiveMenu((current) => (current === item.id ? 0 : item.id));
  };
  const [shopList, setShopList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showStatus, setShowStatus] = useState("shops");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(localStorage.getItem("shopSort") || "");
  const [userCoords, setUserCoords] = useState(null);
  const [noOrderFilter, setNoOrderFilter] = useState(localStorage.getItem("shopNoOrderFilter") || "");
  const [insightFilter, setInsightFilter] = useState(loadShopInsightFilter());
  const [showInsightFilterModal, setShowInsightFilterModal] = useState(false);
  const fetchShops = (
    nextSort = sortBy,
    nextCoords = userCoords,
    resetList = true,
    nextNoOrderFilter = noOrderFilter,
    searchOverride,
    nextInsightFilter = insightFilter
  ) => {
    if (!getAuthToken()) {
      setLoading(false);
      return;
    }

    if (resetList) {
      setPages({});
      setHasMore(true);
      setLoading(true);
      scrollPageToTop();
    }
    setShowStatus("shops");
    const shopsUrl = buildShopsUrl(
      searchOverride ?? searchText,
      nextSort,
      nextCoords,
      null,
      nextNoOrderFilter,
      nextInsightFilter
    );
    const config = {
      url: shopsUrl,
      method: "GET",
    };

    if (resetList) {
      const cached = readShopListCache(shopsUrl);
      if (cached) {
        setShopList(cached.shops);
        setPages(cached.paginate || {});
        setHasMore(resolveProductListHasMore(cached.paginate, cached.shops.length));
        setLoading(false);
      }
    }

    axios(config)
      .then((res) => {
        setLoading(false);
        res &&
          res.data &&
          res.data.data &&
          res.data.data.shops &&
          setShopList((prevShops) => {
            const nextShops = resetList
              ? res.data.data.shops
              : [...prevShops, ...res.data.data.shops];
            const paginate = res?.data?.data?.paginate;
            setHasMore(resolveProductListHasMore(paginate, nextShops.length));
            return nextShops;
          });
        res &&
          res.data &&
          res.data.data &&
          res.data.data.paginate &&
          setPages(res.data.data.paginate);
        if (resetList && res?.data?.data?.shops) {
          writeShopListCache(shopsUrl, {
            shops: res.data.data.shops,
            paginate: res.data.data.paginate,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        const message = getApiErrorMessage(err, "خطا در دریافت لیست فروشگاه‌ها");
        if (!message) {
          return;
        }
        toast.error(message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  };
  const getShopsHandler = () => {
    fetchShops(sortBy, userCoords, true);
  };
  const handleSortSelect = (sortKey) => {
    setShowSortModal(false);
    setActiveMenu(0);

    if (sortKey === "nearest") {
      if (!navigator.geolocation) {
        navigate("/panel/enable-location", { state: { sort: "nearest", from: "/" } });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserCoords(coords);
          setSortBy("nearest");
          localStorage.setItem("shopSort", "nearest");
          fetchShops("nearest", coords, true, noOrderFilter);
        },
        () => {
          navigate("/panel/enable-location", { state: { sort: "nearest", from: "/" } });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
      return;
    }

    setUserCoords(null);
    setSortBy(sortKey);
    localStorage.setItem("shopSort", sortKey);
    fetchShops(sortKey, null, true, noOrderFilter);
  };
  const handleCalendarClick = () => {
    if (showStatus !== "shops") {
      getShopsHandler();
      return;
    }
    if (!localStorage.getItem("shopNoOrderFilterIntroSeen")) {
      setShowOrderInactivityIntro(true);
      return;
    }
    setShowOrderInactivityModal(true);
  };
  const handleOrderInactivityIntroContinue = () => {
    localStorage.setItem("shopNoOrderFilterIntroSeen", "1");
    setShowOrderInactivityIntro(false);
    setShowOrderInactivityModal(true);
  };
  const handleNoOrderFilterSelect = (filterKey) => {
    setShowOrderInactivityModal(false);
    setActiveMenu(0);
    if (filterKey === "reset") {
      setNoOrderFilter("");
      localStorage.removeItem("shopNoOrderFilter");
      fetchShops(sortBy, userCoords, true, "");
      return;
    }
    setNoOrderFilter(filterKey);
    localStorage.setItem("shopNoOrderFilter", filterKey);
    fetchShops(sortBy, userCoords, true, filterKey, undefined, insightFilter);
  };
  const handleInsightFilterApply = (nextFilter) => {
    setInsightFilter(nextFilter);
    saveShopInsightFilter(nextFilter);
    setActiveMenu(0);
    fetchShops(sortBy, userCoords, true, noOrderFilter, undefined, nextFilter);
  };
  const [products, setProducts] = useState([]);
  const [showProductFilter, setShowProductFilter] = useState(false);
  const [showProductSort, setShowProductSort] = useState(false);
  const [productSort, setProductSort] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [productFilterTab, setProductFilterTab] = useState(0);
  const [showInventory, setShowInventory] = useState(false);
  const [catList, setCatList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [subCat, setSubCat] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseList, setWarehouseList] = useState([]);
  const [shopCatList, setShopCatList] = useState([]);

  const getProductQueryParams = () => ({
    searchText,
    filter: hasActiveProductFilter({
      selectedCat,
      selectedBrand,
      subCat,
      selectedSubcategory,
      selectedWarehouse,
      onlyAvailable: showInventory,
    })
      ? 1
      : 0,
    selectedCat,
    selectedBrand,
    subCat,
    selectedSubcategory,
    selectedWarehouse,
    onlyAvailable: showInventory,
    productSort,
    priceFrom,
    priceTo,
  });

  const loadProductFilterOptions = () => {
    axios({ url: `${baseUrl}/categories`, method: "GET" })
      .then((res) => setCatList(res.data.data || []))
      .catch(() => setCatList([]));

    axios({ url: `${baseUrl}/company-categories`, method: "GET" })
      .then((res) => setShopCatList(res.data.data || []))
      .catch(() => setShopCatList([]));

    axios({ url: `${baseUrl}/warehouses`, method: "GET" })
      .then((res) => setWarehouseList(res.data.data || []))
      .catch(() => setWarehouseList([]));
  };

  useEffect(() => {
    if (showProductFilter) {
      loadProductFilterOptions();
    }
  }, [showProductFilter]);

  const productCategoryHandler = (id) => {
    setSelectedCat(id);
    setSelectedSubcategory(null);
    Promise.all([
      axios({ url: `${baseUrl}/brands?category_id=${id}`, method: "GET" }),
      axios({ url: `${baseUrl}/subcategories?category_id=${id}`, method: "GET" }),
    ])
      .then(([brandsRes, subsRes]) => {
        setBrandList(brandsRes.data.data || []);
        const subs = subsRes.data.data || [];
        setSubcategoryList(subs);
        setProductFilterTab(subs.length > 0 ? 4 : 2);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "خطا در دریافت فیلترها", {
          position: "top-center",
          autoClose: 5000,
        });
      });
  };

  const fetchProducts = (overrides = {}, resetList = true) => {
    if (resetList) {
      setPages({});
      setHasMore(true);
      setLoading(true);
      setShowStatus("products");
    }

    const params = {
      ...getProductQueryParams(),
      productSort: overrides.sort ?? productSort,
      priceFrom: overrides.priceFrom ?? priceFrom,
      priceTo: overrides.priceTo ?? priceTo,
      searchText: overrides.searchText ?? searchText,
    };
    const activeSort = params.productSort;
    const url = buildProductListUrl(`${baseUrl}/all-products`, params, {
      count: 10,
      page: resetList ? null : overrides.page,
    });

    if (resetList) {
      const cached = readProductListCache(url);
      if (cached) {
        const cachedProducts = orderProductListForDisplay(cached.products, activeSort);
        setProducts(cachedProducts);
        setPages(cached.paginate || {});
        setHasMore(resolveProductListHasMore(cached.paginate, cachedProducts.length));
        setLoading(false);
      }
    }

    axios({ url, method: "GET" })
      .then((res) => {
        setLoading(false);
        setShowProductFilter(false);
        setShowProductSort(false);
        const nextProducts = (res?.data?.data?.products || []).map((item) => item);
        const paginate = res?.data?.data?.paginate;

        if (resetList) {
          const sortedProducts = orderProductListForDisplay(nextProducts, activeSort);
          setProducts(sortedProducts);
          setPages(paginate || {});
          setHasMore(resolveProductListHasMore(paginate, sortedProducts.length));
          if (res?.data?.data?.products) {
            writeProductListCache(url, { products: nextProducts, paginate });
          }
          return;
        }

        setProducts((prevData) => {
          const merged = orderProductListForDisplay(
            [...prevData, ...nextProducts],
            activeSort
          );
          setHasMore(resolveProductListHasMore(paginate, merged.length));
          return merged;
        });
        setPages(paginate || {});
      })
      .catch((err) => {
        setLoading(false);
        const message = getApiErrorMessage(err, "خطا در دریافت اطلاعات");
        if (!message) {
          return;
        }
        toast.error(message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  };

  const getProductsHandler = () => {
    fetchProducts({}, true);
  };

  const handleProductPreviewSearch = () => {
    if (showStatus === "products") {
      fetchProducts({}, true);
      return;
    }
    getProductsHandler();
  };

  const handleProductSortApply = ({ sort, priceFrom: nextFrom, priceTo: nextTo }) => {
    const nextSort = sort || "";
    const nextPriceFrom = nextFrom || "";
    const nextPriceTo = nextTo || "";
    setProductSort(nextSort);
    setPriceFrom(nextPriceFrom);
    setPriceTo(nextPriceTo);
    setShowProductSort(false);
    fetchProducts(
      { sort: nextSort, priceFrom: nextPriceFrom, priceTo: nextPriceTo },
      true
    );
  };

  const handleProductFilterApply = () => {
    fetchProducts({}, true);
  };
  useEffect(() => {
    if (!getAuthToken()) {
      return;
    }

    const savedNoOrder = localStorage.getItem("shopNoOrderFilter") || "";
    const pendingSearch = sessionStorage.getItem("shopSearchQuery") || "";
    if (pendingSearch) {
      sessionStorage.removeItem("shopSearchQuery");
      setSearchText(pendingSearch);
    }

    if (routeLocation.state?.sort === "nearest" && routeLocation.state?.coords) {
      setSortBy("nearest");
      setUserCoords(routeLocation.state.coords);
      localStorage.setItem("shopSort", "nearest");
      fetchShops("nearest", routeLocation.state.coords, true, savedNoOrder, pendingSearch);
      navigate(routeLocation.pathname, { replace: true, state: {} });
      return;
    }

    const savedSort = localStorage.getItem("shopSort") || "";
    if (savedSort === "nearest" && navigator.geolocation) {
      fetchShops("", null, true, savedNoOrder, pendingSearch);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserCoords(coords);
          setSortBy("nearest");
          fetchShops("nearest", coords, true, savedNoOrder, pendingSearch);
        },
        () => {
          setSortBy("");
          localStorage.removeItem("shopSort");
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 }
      );
      return;
    }

    fetchShops(savedSort, null, true, savedNoOrder, pendingSearch);
  }, []);

  useEffect(() => {
    if (!routeLocation.state?.applyInsightFilter) {
      return;
    }
    const filter = loadShopInsightFilter();
    setInsightFilter(filter);
    fetchShops(sortBy, userCoords, true, noOrderFilter, searchText, filter);
    navigate(routeLocation.pathname, { replace: true, state: {} });
  }, [routeLocation.state?.applyInsightFilter]);

  const [showSortModal, setShowSortModal] = useState(false);
  const [showOrderInactivityModal, setShowOrderInactivityModal] = useState(false);
  const [showOrderInactivityIntro, setShowOrderInactivityIntro] = useState(false);
  const [pages, setPages] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const pageHandler = () => {
    if (!pages?.nextPage || !hasMore) {
      return;
    }

    const config = {
      url: buildShopsUrl(searchText, sortBy, userCoords, pages.nextPage, noOrderFilter, insightFilter),
      method: "GET",
    };

    axios(config)
      .then((res) => {
        res &&
          res.data &&
          res.data.data &&
          res.data.data.shops &&
          setShopList((prevData) => {
            const nextShops = [...prevData, ...res.data.data.shops];
            const paginate = res?.data?.data?.paginate;
            setHasMore(resolveProductListHasMore(paginate, nextShops.length));
            return nextShops;
          });
        res &&
          res.data &&
          res.data.data &&
          res.data.data.paginate &&
          setPages(res.data.data.paginate);
      })
      .catch((err) => {
        const message = getApiErrorMessage(err, "خطا در دریافت اطلاعات");
        if (!message) {
          return;
        }
        toast.error(message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  };
  const pageHandlerProduct = () => {
    if (!pages?.nextPage || !hasMore) {
      return;
    }

    fetchProducts({ page: pages.nextPage }, false);
  };
  const [showWallete, setShowWallete] = useState(false);
  const [selectedShop, setSelectedShop] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [constPhoneNumber, setConstPhoneNumber] = useState("");
  const [showCallModal, setShowCallModal] = useState(false);
  const [gift, setGift] = useState([]);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [description, setDescription] = useState([]);
  const [showDescription, setShowDescription] = useState(false);
  const [itemLoc, setItemLoc] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);

  // Gallery
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);

  const openGallery = (images) => {
    setGalleryImages(images);
    setShowGallery(true);
  };

  const closeGallery = () => {
    setShowGallery(false);
  };
  //
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [shopForCrm, setShopForCrm] = useState(null);
  const [creditBlock, setCreditBlock] = useState(null);

  const openShopProducts = (item) => {
    if (activeMenu === item.id) {
      closeShopQuickActions();
      return;
    }

    prefetchShopProducts(axios, baseUrl, item.id);
    navigate("/products/" + item.id);
  };

  const warmShopProducts = (shopId) => {
    prefetchShopProducts(axios, baseUrl, shopId);
  };

  return (
    <div className="container">
      {showGiftModal && <GiftModal gift={gift} />}
      {showDescription && <DescriptionModal description={description} />}
      {showMapModal && <MapModal item={itemLoc} />}
      {showCommentModal && shopForCrm && (
        <ShopCrmModal shop={shopForCrm} setShow={setShowCommentModal} />
      )}
      <CreditPaymentBlockModal
        visible={!!creditBlock}
        mode={creditBlock?.type === "expired" ? "expired" : "payment"}
        message={creditBlock?.message}
        count={creditBlock?.count || 0}
        orders={creditBlock?.orders || []}
        onConfirm={() => {
          if (!creditBlock?.orders?.length) {
            setCreditBlock(null);
            return;
          }
          navigateToCreditPayment(
            navigate,
            creditBlock.orders[0].id,
            creditBlock.customerId,
            "/"
          );
          setCreditBlock(null);
        }}
        onDismiss={() => setCreditBlock(null)}
      />
      {showGallery && (
        <Gallery images={galleryImages} onClose={closeGallery} />
      )}
      {showProductSort && (
        <ProductSortModal
          activeSort={productSort}
          priceFrom={priceFrom}
          priceTo={priceTo}
          onApply={handleProductSortApply}
          onClose={() => setShowProductSort(false)}
        />
      )}
      {showSortModal && (
        <SortModal
          activeSort={sortBy}
          onSelect={handleSortSelect}
          onClose={() => setShowSortModal(false)}
        />
      )}
      {showOrderInactivityIntro && (
        <OrderInactivityIntroModal
          onContinue={handleOrderInactivityIntroContinue}
          onClose={() => setShowOrderInactivityIntro(false)}
        />
      )}
      {showOrderInactivityModal && (
        <OrderInactivityFilterModal
          activeFilter={noOrderFilter}
          onSelect={handleNoOrderFilterSelect}
          onClose={() => setShowOrderInactivityModal(false)}
        />
      )}
      {showInsightFilterModal && (
        <ShopInsightFilterModal
          activeFilter={insightFilter}
          onApply={handleInsightFilterApply}
          onClose={() => setShowInsightFilterModal(false)}
        />
      )}
      {(showProductFilter ||
        showProductSort ||
        showSortModal ||
        showOrderInactivityModal ||
        showOrderInactivityIntro ||
        showInsightFilterModal ||
        showWallete ||
        showGiftModal ||
        showMapModal ||
        showCommentModal ||
        showDescription ||
        showCallModal) && (
          <div
            onClick={() => {
              closeShopQuickActions();
              setShowProductFilter(false);
              setShowProductSort(false);
              setShowSortModal(false);
              setShowOrderInactivityModal(false);
              setShowOrderInactivityIntro(false);
              setShowInsightFilterModal(false);
              setShowCommentModal(false);
              setShowWallete(false);
              setShowCallModal(false);
              setShowMapModal(false);
              setShowGiftModal(false);
              setShowDescription(false);
            }}
            id="bg_gray"
            className="bg-gray"
          ></div>
        )}
      {showWallete && <Wallete shop={selectedShop} />}
      <div id="bg_gray2" className="bg-gray d-none"></div>
      {showCallModal && (
        <CallModal
          storeName={storeName}
          phone={phoneNumber}
          constPhone={constPhoneNumber}
        />
      )}
      <div className="search-box" id="search_box">
        <div className="pos-rel-search">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={createSearchEnterHandler(
              showStatus === "products" ? handleProductPreviewSearch : getShopsHandler
            )}
            type="text"
            placeholder={
              translation.toast_error003[lang]
                ? translation.toast_error003[lang]
                : "جستجو کنید"
            }
          />
          <svg
            onClick={() =>
              showStatus === "products"
                ? handleProductPreviewSearch()
                : getShopsHandler()
            }
            className="search-svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.5831 13.5489C19.204 11.7018 19.1293 9.69155 18.3731 7.89557C17.6169 6.09959 16.2312 4.64137 14.4761 3.79465C12.7209 2.94793 10.7171 2.77094 8.84074 3.29691C6.96438 3.82287 5.3445 5.01562 4.28521 6.65125C3.22592 8.28688 2.80005 10.2529 3.08754 12.1803C3.37504 14.1076 4.35614 15.8638 5.84666 17.1191C7.33718 18.3744 9.23463 19.0424 11.1828 18.9979C13.131 18.9534 14.9959 18.1993 16.4275 16.8772"
              stroke="#A2A2A2"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.5 16.958L21.5 21.958"
              stroke="#A2A2A2"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div
          className="svg-rec"
          onClick={() => {
            showStatus == "shops"
              ? getProductsHandler()
              : window.location.reload();
          }}
        >
          <svg
            width="46"
            height="46"
            viewBox="0 0 46 46"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="45"
              height="45"
              rx="4.5"
              fill="#cdedff"
              stroke="#3ba7e4"
            />
            <path
              d="M14.17 18.4399L23 23.5499L31.77 18.4699"
              stroke="#00A3FF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M23 32.61V23.54"
              stroke="#00A3FF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.39 20.1701C13.39 18.7901 14.38 17.1101 15.59 16.4401L20.93 13.4701C22.07 12.8401 23.94 12.8401 25.08 13.4701L30.42 16.4401C31.63 17.1101 32.62 18.7901 32.62 20.1701V25.8201C32.62 27.2001 31.63 28.8801 30.42 29.5501L25.08 32.5201C23.94 33.1501 22.07 33.1501 20.93 32.5201L15.59 29.5501C14.38 28.8801 13.39 27.2001 13.39 25.8201V25.0001"
              stroke="#00A3FF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M28 24.2401V20.5801L21.93 17.0701L20.88 16.4701L18.51 15.1001"
              stroke="#00A3FF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div
          className={`svg-rec ${
            showStatus === "products"
              ? hasActiveProductSort({ productSort, priceFrom, priceTo })
                ? "svg-rec-active-sort"
                : ""
              : sortBy
                ? "svg-rec-active-sort"
                : ""
          }`}
          onClick={() => {
            if (showStatus === "products") {
              setShowProductSort(true);
              return;
            }
            setShowSortModal(true);
          }}
        >
          <AppIcon name="sort" width={24} height={24} alt="مرتب‌سازی" />
        </div>
        {showStatus === "products" ? (
          <div
            className={`svg-rec ${
              hasActiveProductFilter({
                selectedCat,
                selectedBrand,
                subCat,
                selectedSubcategory,
                selectedWarehouse,
                onlyAvailable: showInventory,
              })
                ? "svg-rec-active-filter"
                : ""
            }`}
            onClick={() => setShowProductFilter(true)}
          >
            <SearchBarProductFilterIcon />
          </div>
        ) : (
          <>
            <div
              className={`svg-rec ${noOrderFilter ? "svg-rec-active-filter" : ""}`}
              onClick={handleCalendarClick}
            >
              <AppIcon name="calendar" width={24} height={24} alt="فیلتر ثبت سفارش" />
            </div>
            <ShopInsightFilterButton
              active={hasActiveShopInsightFilter(insightFilter)}
              onClick={() => setShowInsightFilterModal(true)}
            />
            <SearchBarAddShopIcon onClick={() => navigate("/panel/add_shop")} />
          </>
        )}
      </div>
      {showStatus && showStatus == "shops" ? (
        loading ? (
          <ShopListSkeleton />
        ) : shopList && shopList.length !== 0 ? (
          <div className="shop-main" id="shops">
            <VisitorDailyDashboard />
            <ShopCreditLegend />
            <InfiniteScroll
              dataLength={shopList.length}
              next={pageHandler}
              hasMore={hasMore}
              loader={
                shopList.length !== 0 && <LoadMoreSkeleton variant="shop" />
              }
            >
              {shopList &&
                shopList.length !== 0 &&
                shopList.map((item) => {
                  return (
                    <div
                      onClick={() => openShopProducts(item)}
                      onMouseEnter={() => warmShopProducts(item.id)}
                      onTouchStart={() => warmShopProducts(item.id)}
                      key={item.id}
                      className={`shop-card${activeMenu === item.id ? " is-menu-open" : ""}${getShopCreditAlertClass(item.credit_alert)}`}
                    >
                      <img
                        src={item.avatar}
                        alt="shop name"
                      />
                      <div>
                        <span className="name">
                          {item.store_name}
                        </span>
                        <span className="customer-name">
                          {item.name} {item.family}
                        </span>
                        <span className="address">
                          {item.address}
                        </span>
                        <ShopCardMeta shop={item} />
                      </div>
                      {activeMenu !== item.id && (
                      <div
                        className="blue-circle"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMenuHandler(item);
                        }}
                      >
                        <svg
                          width="19"
                          height="19"
                          viewBox="0 0 19 19"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="7"
                            height="7"
                            rx="2.5"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                          <rect
                            x="1"
                            y="11"
                            width="7"
                            height="7"
                            rx="2.5"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                          <rect
                            x="11"
                            y="1"
                            width="7"
                            height="7"
                            rx="2.5"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                          <rect
                            x="11"
                            y="11"
                            width="7"
                            height="7"
                            rx="2.5"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </div>
                      )}
                      <div
                        className={
                          activeMenu == item.id
                            ? "open-menu "
                            : "open-menu close"
                        }
                        id={"menu-" + item.id}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPhoneNumber(
                              item.phone
                            );
                            setConstPhoneNumber(
                              item.mobile
                            );
                            setStoreName(
                              item.store_name
                            );
                            setActiveMenu(0);
                            setShowCallModal(true);
                          }}
                        >
                          <path
                            d="M7.90717 3.36067C7.00763 2.79874 5.82782 2.89183 5.06847 3.65118L3.7817 4.93795C1.82739 6.89227 3.66372 11.8972 7.88327 16.1167C12.1028 20.3363 17.1077 22.1726 19.0621 20.2183L20.3488 18.9315C21.2371 18.0432 21.2137 16.5795 20.2964 15.6622L18.3033 13.6691C17.386 12.7518 15.9222 12.7283 15.0339 13.6166L14.7575 13.893C14.2779 14.3726 13.5042 14.4196 12.9641 13.9739C12.4432 13.544 11.9244 13.0806 11.4219 12.5781C10.9194 12.0756 10.456 11.5568 10.0261 11.0359C9.58045 10.4958 9.62737 9.72209 10.107 9.24247L10.3834 8.96609C11.0837 8.26578 11.2173 7.20785 10.7931 6.34197"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowWallete(true);
                            setSelectedShop(
                              item.id
                            );
                            setActiveMenu(0);
                          }}
                        >
                          <path
                            d="M2 16.5V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V11C22 8.23858 19.7614 6 17 6H7C4.23858 6 2 8.23858 2 11V12"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M19 6.5V6.33914C19 4.0991 16.944 2.42329 14.75 2.875V2.875L10.5 3.75L5.99174 4.67817C3.66769 5.15665 2 7.20267 2 9.57546L2 12"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M6 17.5H12"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M22 16.5V17.25H22.75V16.5H22ZM18.5 12.25C18.9142 12.25 19.25 11.9142 19.25 11.5C19.25 11.0858 18.9142 10.75 18.5 10.75V12.25ZM21.25 11.5V16.5H22.75V11.5H21.25ZM22 15.75H17.5V17.25H22V15.75ZM17.5 12.25H18.5V10.75H17.5V12.25ZM17.5 15.75C16.5335 15.75 15.75 14.9665 15.75 14H14.25C14.25 15.7949 15.7051 17.25 17.5 17.25V15.75ZM15.75 14C15.75 13.0335 16.5335 12.25 17.5 12.25V10.75C15.7051 10.75 14.25 12.2051 14.25 14H15.75Z"
                            fill="#F2F2F2"
                          />
                          <path
                            d="M17.5 14H17.7"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {item.lat &&
                          item.lng &&
                          item.lat !== "null" &&
                          item.lng !== "null" ? (
                          <svg
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemLoc(item);
                              setShowMapModal(true);
                              setActiveMenu(0);
                            }}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.12 10.3099C15.12 12.0299 13.73 13.4299 12 13.4299C10.27 13.4299 8.88 12.0399 8.88 10.3099C8.88 8.57994 10.28 7.18994 12 7.18994C12.34 7.18994 12.67 7.23994 12.97 7.33994"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.97996 4.30006C10.35 0.190061 18.82 1.60006 20.38 8.51006C21.53 13.5901 18.37 17.8901 15.6 20.5501C13.59 22.4901 10.41 22.4901 8.38996 20.5501C5.62996 17.8801 2.45996 13.5801 3.61996 8.50006"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <path
                              d="M10.42 12.9999C9.5 12.4599 8.88 11.4599 8.88 10.3099C8.88 8.58994 10.27 7.18994 12 7.18994C13.15 7.18994 14.15 7.80994 14.69 8.73994"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.51001 3.83995C10.05 1.04996 15.71 1.42996 18.68 4.97996"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.98995 17.81C4.14995 15.3 2.80995 12.09 3.62995 8.48999"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20.38 8.5C21.53 13.58 18.37 17.88 15.6 20.54C13.59 22.48 10.41 22.48 8.39001 20.54"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M22 2L2 22"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}

                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              "/panel/edit/" +
                              item.id
                            );
                            setActiveMenu(0);
                          }}
                        >
                          <path
                            d="M21 13V17C21 19.7614 18.7614 22 16 22H8C5.23858 22 3 19.7614 3 17V9C3 6.23858 5.23858 4 8 4H12"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M13.2958 6.87469L16.7465 3.4187C17.527 2.63706 18.7928 2.63658 19.5739 3.41763L21.2334 5.07712C22.0078 5.85151 22.0158 7.10516 21.2514 7.88943L13.8224 15.5116C13.258 16.0907 12.4839 16.4172 11.6755 16.4172L9.40332 16.4171C8.76638 16.4171 8.25772 15.8861 8.28451 15.2493V15.2493L8.33363 14.0815L8.38275 12.9137V12.9137C8.41434 12.1627 8.72648 11.4509 9.25741 10.9192L10.8638 9.31032"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M17.678 7.60687L19.3641 9.29297"
                            stroke="#F2F2F2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <div
                          className="blue-circle-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCommentModal(true);
                            setActiveMenu(0);
                            setShopForCrm(item);
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.5 19H8C4 19 2 18 2 13V8C2 4 4 2 8 2H16C20 2 22 4 22 8V13C22 17 20 19 16 19H15.5C15.19 19 14.89 19.15 14.7 19.4L13.2 21.4C12.54 22.28 11.46 22.28 10.8 21.4L9.3 19.4C9.14 19.18 8.77 19 8.5 19Z"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M7 8H17"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M7 13H13"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </InfiniteScroll>
          </div>
        ) : (
          <div className="emp-main">
            <svg
              width="145"
              height="145"
              viewBox="0 0 145 145"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M127.177 70.7481V106.938C127.177 123.613 113.644 137.146 96.9687 137.146H44.1646C42.775 137.146 41.3854 137.025 40.0563 136.844C42.2917 135.877 44.2854 134.548 46.0979 132.917C46.3396 132.735 46.5207 132.554 46.702 132.313C48.3332 130.923 49.7228 129.171 50.8707 127.237C53.1061 123.612 54.375 119.323 54.375 114.792C54.375 108.448 51.8979 102.709 47.9104 98.3585C47.3062 97.6939 46.6415 97.029 45.9165 96.4852C41.6874 92.7998 36.25 90.625 30.2083 90.625C25.9188 90.625 21.8707 91.773 18.3665 93.7668C17.2186 94.371 16.1313 95.0959 15.1042 95.9417C14.6813 96.2438 14.3187 96.6062 13.9562 96.9687V71.171C18.5479 76.1252 25.073 78.9646 32.1417 78.9646C39.7542 78.9646 47.0645 75.1583 51.6562 69.0562C55.7645 75.1583 62.7729 78.9646 70.5666 78.9646C78.3 78.9646 85.1874 75.3394 89.3561 69.2977C94.0082 75.279 101.198 78.9646 108.69 78.9646C116 78.9646 122.646 76.0043 127.177 70.7481Z"
                fill="#3BA7E4"
              />
              <path
                d="M88.631 9.48547H52.381L47.9102 53.952C47.5477 58.0604 48.1518 61.9272 49.6622 65.4314C53.1663 73.648 61.3831 78.9646 70.5665 78.9646C79.8706 78.9646 87.9063 73.7689 91.5313 65.4918C92.6188 62.8939 93.2833 59.8731 93.3437 56.7918V55.6439L88.631 9.48547Z"
                fill="#3BA7E4"
              />
              <path
                opacity="0.6"
                d="M133.158 51.8981L131.467 35.1021C128.929 16.8562 120.652 9.42499 102.95 9.42499H79.7501L84.2208 54.7375C84.2813 55.3417 84.3418 56.0064 84.3418 57.1543C84.7043 60.296 85.6707 63.1959 87.1207 65.7938C91.4707 73.7688 99.9292 78.9042 108.75 78.9042C116.785 78.9042 124.036 75.3395 128.567 69.0562C132.131 64.2833 133.762 58.1814 133.158 51.8981Z"
                fill="#3BA7E4"
              />
              <path
                opacity="0.6"
                d="M37.8813 9.48547C20.1188 9.48547 11.9019 16.9165 9.30398 35.3436L7.67298 51.9582C7.06881 58.4228 8.82094 64.7064 12.6272 69.6001C17.2189 75.5814 24.2875 78.9646 32.1417 78.9646C40.9625 78.9646 49.4209 73.8291 53.7104 65.9749C55.2813 63.2562 56.3082 60.1144 56.6103 56.8519L61.323 9.54595H37.8813V9.48547Z"
                fill="#3BA7E4"
              />
              <path
                d="M47.9104 98.3585C47.3062 97.6939 46.6415 97.029 45.9165 96.4852C41.6874 92.7998 36.25 90.625 30.2084 90.625C25.9188 90.625 21.8707 91.773 18.3666 93.7668C17.2186 94.371 16.1313 95.0959 15.1042 95.9417C9.54585 100.352 6.04169 107.179 6.04169 114.792C6.04169 119.323 7.31054 123.612 9.54596 127.237C10.0897 128.204 10.7543 129.11 11.4793 129.956C11.6002 130.138 11.7811 130.258 11.9019 130.44C16.2519 135.636 22.8375 138.958 30.2084 138.958C36.3104 138.958 41.8688 136.723 46.098 132.917C46.3396 132.735 46.5207 132.554 46.702 132.313C48.3332 130.923 49.7228 129.171 50.8708 127.237C53.1062 123.612 54.375 119.323 54.375 114.792C54.375 108.448 51.8979 102.709 47.9104 98.3585ZM39.2709 119.202H34.7396V123.915C34.7396 126.392 32.6854 128.446 30.2084 128.446C27.7313 128.446 25.6771 126.392 25.6771 123.915V119.202H21.1459C18.6688 119.202 16.6146 117.148 16.6146 114.671C16.6146 112.194 18.6688 110.139 21.1459 110.139H25.6771V105.85C25.6771 103.373 27.7313 101.319 30.2084 101.319C32.6854 101.319 34.7396 103.373 34.7396 105.85V110.139H39.2709C41.7479 110.139 43.8021 112.194 43.8021 114.671C43.8021 117.148 41.7479 119.202 39.2709 119.202Z"
                fill="#3BA7E4"
              />
            </svg>

            <p className="emp-text">
              {translation.toast_error022[lang]
                ? translation.toast_error022[lang]
                : "هیچ فروشگاهی ثبت نشده است."}
            </p>
            <small className="cus-text">در صورتیکه مشتری ثبت کرده اید صفحه را رفرش کنید</small>
            <a
              onClick={() => navigate("/panel/add_shop")}
              className="emp-a"
            >
              {translation.Building_a_store_registration[lang]
                ? translation.Building_a_store_registration[
                lang
                ]
                : "ثبت فروشگاه"}
            </a>
          </div>
        )
      ) : (
        <div className="product-main">
          {(showProductFilter || showProductSort) && (
            <div
              onClick={() => {
                setShowProductFilter(false);
                setShowProductSort(false);
              }}
              id="bg_gray"
              className="bg-gray"
            />
          )}
          {showProductFilter && (
            <ProductFilterModal
              lang={lang}
              showFilterTab={productFilterTab}
              setShowFilterTab={setProductFilterTab}
              showInventory={showInventory}
              setShowInventory={setShowInventory}
              catList={catList}
              brandList={brandList}
              shopCatList={shopCatList}
              subcategoryList={subcategoryList}
              warehouseList={warehouseList}
              selectedCat={selectedCat}
              selectedBrand={selectedBrand}
              subCat={subCat}
              selectedSubcategory={selectedSubcategory}
              selectedWarehouse={selectedWarehouse}
              onCategorySelect={productCategoryHandler}
              onBrandSelect={setSelectedBrand}
              onSubCatSelect={setSubCat}
              onSubcategorySelect={setSelectedSubcategory}
              onWarehouseSelect={setSelectedWarehouse}
              onApply={handleProductFilterApply}
              onClose={() => setShowProductFilter(false)}
            />
          )}
          <h2>
            {" "}
            {translation.Preview_of_goods_Preview[lang]
              ? translation.Preview_of_goods_Preview[lang]
              : "پیش نمایش کالا ها"}
          </h2>
          {loading ? (
            <ProductListSkeleton />
          ) : (
            <InfiniteScroll
              dataLength={products.length}
              next={pageHandlerProduct}
              hasMore={hasMore}
              loader={
                products.length !== 0 && <LoadMoreSkeleton variant="product" />
              }
            >
              {products &&
                products.length !== 0 &&
                products.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className={getProductCardClassName(item)}
                    >
                      <div className="row-1">
                        <div className="img-div">
                          {item.Images &&
                            item.Images[0] ? (
                            <div
                              className="img-div"
                              onClick={() =>
                                openGallery(
                                  item.Images
                                )
                              }
                            >
                              <img
                                src={
                                  item
                                    .Images[0]
                                    .image
                                }
                                alt=""
                              />
                            </div>
                          ) : (
                            <div className="img-div">
                              <img
                                src={
                                  item.Images
                                }
                                alt=""
                              />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="marquee">
                            <h4
                              className={
                                item.name
                                  .length < 13
                                  ? "name"
                                  : item.name
                                    .length <
                                    25
                                    ? "name moving-2"
                                    : "name moving"
                              }
                            >
                              <span>
                                {item.name}
                              </span>
                            </h4>
                          </div>
                          <div className="d-flex-center product-highlight-row">
                            <span className="price-span-1">
                              {translation
                                .Products_Unit[
                                lang
                              ]
                                ? translation
                                  .Products_Unit[
                                lang
                                ]
                                : "قیمت واحد"}
                              :
                            </span>
                            <span className="price-span-2">
                              {Number(
                                item.unit_price
                              ).toLocaleString()}{" "}
                              {currency &&
                                currency}
                            </span>
                          </div>
                          <div className="d-flex-center product-highlight-row">
                            <span className="muted-span-1">
                              {translation
                                .Products_Store[
                                lang
                              ]
                                ? translation
                                  .Products_Store[
                                lang
                                ]
                                : "قیمت فروشگاه"}
                              :
                            </span>
                            <span className="muted-span-2 product-highlight-value">
                              {Number(
                                item.store_price
                              ).toLocaleString()}{" "}
                              {currency &&
                                currency}
                            </span>
                          </div>
                          <div className="d-flex-center product-highlight-row">
                            <span className="muted-span-1">
                              {translation
                                .Products_customer_profit[
                                lang
                              ]
                                ? translation
                                  .Products_customer_profit[
                                lang
                                ]
                                : "سود مشتری"}
                              :
                            </span>
                            <span className="muted-span-2 product-highlight-value">
                              {formatCustomerProfitPercent(
                                item.customer_profit_percent ??
                                  calcCustomerProfitPercent(
                                    item.store_price,
                                    item.consumer_price
                                  )
                              )}
                            </span>
                          </div>
                          <div className="d-flex-center product-highlight-row">
                            <span className="muted-span-1">
                              {translation
                                .Products_Quantity[
                                lang
                              ]
                                ? translation
                                  .Products_Quantity[
                                lang
                                ]
                                : "تعداد در واحد"}
                              :{" "}
                            </span>
                            <span className="muted-span-2 product-unit-count product-highlight-value">
                              {item.unit_count}{" "}
                              {
                                item
                                  .measure_unit
                                  .name
                              }
                            </span>
                          </div>
                        </div>
                        <div
                          className="gift"
                          onClick={() => {
                            setGift(item.Gifts);
                            setShowGiftModal(true);
                          }}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.9775 7.5H2.97754V13.5C2.97754 15.75 3.72754 16.5 5.97754 16.5H11.9775C14.2275 16.5 14.9775 15.75 14.9775 13.5V7.5Z"
                              stroke="#3BA7E4"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16.125 5.25V6C16.125 6.825 15.7275 7.5 14.625 7.5H3.375C2.2275 7.5 1.875 6.825 1.875 6V5.25C1.875 4.425 2.2275 3.75 3.375 3.75H14.625C15.7275 3.75 16.125 4.425 16.125 5.25Z"
                              stroke="#3BA7E4"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M8.73008 3.75008H4.59008C4.33508 3.47258 4.34258 3.04508 4.61258 2.77508L5.67758 1.71008C5.95508 1.43258 6.41258 1.43258 6.69008 1.71008L8.73008 3.75008Z"
                              stroke="#3BA7E4"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M13.4025 3.75008H9.26245L11.3025 1.71008C11.58 1.43258 12.0375 1.43258 12.315 1.71008L13.38 2.77508C13.65 3.04508 13.6575 3.47258 13.4025 3.75008Z"
                              stroke="#3BA7E4"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M6.70508 7.5V11.355C6.70508 11.955 7.36508 12.3075 7.86758 11.985L8.57258 11.52C8.82758 11.355 9.15008 11.355 9.39758 11.52L10.0651 11.97C10.5601 12.3 11.2276 11.9475 11.2276 11.3475V7.5H6.70508Z"
                              stroke="#3BA7E4"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="row-2">
                        <div className="inventory">
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.9999 4.58325L6.92992 1.83325L1.83325 5.49992L5.90325 8.24992L10.9999 4.58325Z"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11 4.58325L15.07 1.83325L20.1667 5.49992L16.0967 8.24992L11 4.58325Z"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.9999 12.8334L6.92992 15.5834L1.83325 11.9167L5.90325 9.16675L10.9999 12.8334Z"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11 12.8334L15.07 15.5834L20.1667 11.9167L16.0967 9.16675L11 12.8334Z"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M17.4166 14.2083V17.2057L11.3849 19.9924C11.1374 20.1024 10.8624 20.1024 10.6149 19.9924L4.58325 17.2057V14.2083"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>
                            {translation
                              .Products_inventory[
                              lang
                            ]
                              ? translation
                                .Products_inventory[
                              lang
                              ]
                              : "موجودی"}
                            :
                          </span>
                          <span className="inv-num">
                            {item.inventory}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex-center pro-row">
                        <div className="d-flex-center">
                          {item.discount_percent
                            ? item.discount_percent !==
                            0 && (
                              <span className="discount">
                                {
                                  item.discount_percent
                                }
                                %
                              </span>
                            )
                            : ""}
                        </div>
                        <svg
                          className="mt-10"
                          width="48"
                          height="28"
                          viewBox="0 0 48 28"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            width="48"
                            height="28"
                            rx="14"
                            fill="#CCEDFF"
                          />
                          <path
                            d="M28.96 8.16992C30.96 9.55992 32.34 11.7699 32.62 14.3199"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.49 14.37C15.75 11.83 17.11 9.61997 19.09 8.21997"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M20.1899 22.9399C21.3499 23.5299 22.6699 23.8599 24.0599 23.8599C25.3999 23.8599 26.6599 23.5599 27.7899 23.0099"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M21.28 6.91989C21.28 8.44989 22.52 9.69989 24.06 9.69989C25.6 9.69989 26.84 8.45989 26.84 6.91989C26.84 5.37989 25.6 4.13989 24.06 4.13989"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.83 21.9201C18.3654 21.9201 19.61 20.6755 19.61 19.1401C19.61 17.6048 18.3654 16.3601 16.83 16.3601C15.2947 16.3601 14.05 17.6048 14.05 19.1401C14.05 20.6755 15.2947 21.9201 16.83 21.9201Z"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M33.9399 19.1401C33.9399 17.6101 32.6999 16.3601 31.1599 16.3601C29.6199 16.3601 28.3799 17.6001 28.3799 19.1401C28.3799 20.6801 29.6199 21.9201 31.1599 21.9201"
                            stroke="#3BA7E4"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      {item.description && (
                        <div className="info-div">
                          <svg
                            onClick={() => {
                              setDescription(
                                item.description
                              );
                              setShowDescription(
                                true
                              );
                            }}
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.4999 22.9166C18.2291 22.9166 22.9166 18.2291 22.9166 12.4999C22.9166 6.77075 18.2291 2.08325 12.4999 2.08325C6.77075 2.08325 2.08325 6.77075 2.08325 12.4999C2.08325 18.2291 6.77075 22.9166 12.4999 22.9166Z"
                              stroke="#FF9A51"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.6"
                              d="M12.5 8.33325V13.5416"
                              stroke="#FF9A51"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.6"
                              d="M12.4944 16.6667H12.5037"
                              stroke="#FF9A51"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
            </InfiniteScroll>
          )}
        </div>
      )}
    </div>
  );
};

export default Shop;
